// PRODUCTION — rich text editor for form text blocks
import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Editor, EditorProvider, Toolbar, Separator, createButton } from 'react-simple-wysiwyg'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Indent,
  Outdent,
  Link2,
  Undo2,
  Redo2,
  RemoveFormatting,
} from 'lucide-react'

const _sz = 15
const IcBold = createButton('Bold', <Bold size={_sz} />, 'bold')
const IcItalic = createButton('Italic', <Italic size={_sz} />, 'italic')
const IcUnderline = createButton('Underline', <Underline size={_sz} />, 'underline')
const IcStrike = createButton('Strikethrough', <Strikethrough size={_sz} />, 'strikeThrough')
const IcOL = createButton('Numbered List', <ListOrdered size={_sz} />, 'insertOrderedList')
const IcUL = createButton('Bullet List', <List size={_sz} />, 'insertUnorderedList')
const IcIndent = createButton('Indent', <Indent size={_sz} />, 'indent')
const IcOutdent = createButton('Outdent', <Outdent size={_sz} />, 'outdent')
const IcUndo = createButton('Undo', <Undo2 size={_sz} />, 'undo')
const IcRedo = createButton('Redo', <Redo2 size={_sz} />, 'redo')
const IcClear = createButton('Clear Formatting', <RemoveFormatting size={_sz} />, 'removeFormat')

const richEditorStyles = `
  .rte-wrap {
    border: 1px solid var(--mui-shades-200);
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .rte-wrap:focus-within {
    border-color: var(--mui-primary-400);
    box-shadow: 0 0 0 3px var(--mui-primary-100);
  }
  .rte-wrap .rsw-editor {
    border: none !important;
    border-radius: 0 !important;
    overflow: visible !important;
    font-family: 'Geist', sans-serif !important;
  }
  .rte-wrap .rsw-toolbar {
    background: var(--mui-shades-0) !important;
    border-bottom: 1px solid var(--mui-shades-100) !important;
    padding: 6px 8px !important;
    gap: 2px !important;
    flex-wrap: wrap !important;
    row-gap: 2px !important;
  }
  .rte-wrap .rsw-toolbar [data-active="true"] {
    background: var(--mui-primary-100) !important;
    color: var(--mui-primary-700) !important;
    border-radius: 6px !important;
  }
  .rte-wrap .rsw-btn {
    width: 32px !important;
    height: 32px !important;
    border-radius: 6px !important;
    color: var(--mui-shades-500) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: background 0.1s, color 0.1s !important;
    position: relative !important;
  }
  .rte-wrap .rsw-btn:hover {
    background: var(--mui-shades-100) !important;
    color: var(--mui-shades-900) !important;
  }
  .rte-wrap .rsw-btn::after {
    content: attr(title);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--mui-shades-900);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 10;
  }
  .rte-wrap .rsw-btn:hover::after { opacity: 1; }
  .rte-wrap .rsw-separator {
    width: 1px !important;
    height: 20px !important;
    background: var(--mui-shades-100) !important;
    margin: 0 6px !important;
    align-self: center !important;
  }
  .rte-wrap .rsw-ce {
    min-height: 140px !important;
    padding: 14px 16px !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: var(--mui-shades-900) !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    font-family: 'Geist', sans-serif !important;
  }
  .rte-wrap .rsw-ce:focus { outline: none !important; }
  .rte-wrap .rsw-ce:empty::before {
    content: 'Start typing...' !important;
    color: var(--mui-shades-400) !important;
    pointer-events: none !important;
  }
  .rte-wrap .rsw-ce h1 { font-size: 34px; font-weight: 700; line-height: 42px; letter-spacing: -0.68px; margin: 16px 0 8px; }
  .rte-wrap .rsw-ce h2 { font-size: 28px; font-weight: 700; line-height: 32px; letter-spacing: -0.56px; margin: 14px 0 6px; }
  .rte-wrap .rsw-ce h3 { font-size: 20px; font-weight: 700; line-height: 26px; margin: 12px 0 4px; }
  .rte-wrap .rsw-ce p { margin: 0 0 8px; }
  .rte-wrap .rsw-ce ul, .rte-wrap .rsw-ce ol { padding-left: 24px; margin: 4px 0 8px; }
  .rte-wrap .rsw-ce li { margin: 2px 0; }
  .rte-wrap .rsw-ce a { color: var(--mui-primary-500); text-decoration: underline; }
  .rte-wrap .rsw-dd { display: none !important; }
  .rte-block-select {
    height: 32px;
    padding: 0 24px 0 10px;
    border-radius: 6px;
    border: 1px solid var(--mui-shades-200);
    font-size: 13px;
    font-weight: 500;
    color: var(--mui-shades-700);
    background-color: var(--mui-shades-0);
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    font-family: 'Geist', sans-serif;
  }
`

const BLOCK_OPTIONS = [
  { tag: 'p', label: 'Body — 14px' },
  { tag: 'h1', label: 'H1 — 34px Bold' },
  { tag: 'h2', label: 'H2 — 28px Bold' },
  { tag: 'h3', label: 'H3 — 20px Bold' },
]

function BlockStyleSelect() {
  const [currentTag, setCurrentTag] = useState('p')

  useEffect(() => {
    const detect = () => {
      const sel = window.getSelection()

      if (!sel || sel.rangeCount === 0) {
        return
      }

      let node: Node | null = sel.anchorNode

      while (node && node.nodeType !== 1) {
        node = node.parentNode
      }

      if (!node) {
        return
      }

      const el = node as HTMLElement
      const block = el.closest('h1, h2, h3, p, div')

      if (block) {
        const tag = block.tagName.toLowerCase()
        setCurrentTag(BLOCK_OPTIONS.find((o) => o.tag === tag) ? tag : 'p')
      } else {
        setCurrentTag('p')
      }
    }

    document.addEventListener('selectionchange', detect)

    return () => document.removeEventListener('selectionchange', detect)
  }, [])

  return (
    <select
      className="rte-block-select"
      value={currentTag}
      onChange={(e) => {
        document.execCommand('formatBlock', false, e.target.value)
        setCurrentTag(e.target.value)
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {BLOCK_OPTIONS.map((o) => (
        <option key={o.tag} value={o.tag}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function RteLinkButton() {
  const [showInput, setShowInput] = useState(false)
  const [url, setUrl] = useState('https://')
  const inputRef = useRef<HTMLInputElement>(null)
  const savedRange = useRef<Range | null>(null)

  const handleOpen = () => {
    const sel = window.getSelection()

    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange()
    }

    setShowInput(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleApply = () => {
    if (!url || url === 'https://') {
      return
    }

    if (savedRange.current) {
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(savedRange.current)
    }

    document.execCommand('createLink', false, url)
    setShowInput(false)
    setUrl('https://')
    savedRange.current = null
  }

  const handleCancel = () => {
    setShowInput(false)
    setUrl('https://')
    savedRange.current = null
  }

  return (
    <div css={tw`relative inline-flex`}>
      <button
        className="rsw-btn"
        title="Link"
        type="button"
        onMouseDown={(e) => {
          e.preventDefault()
          handleOpen()
        }}
        css={[
          tw`flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-md border-0`,
          showInput ? tw`bg-primary-100 text-primary-700` : tw`bg-transparent text-shades-600`,
        ]}
      >
        <Link2 size={_sz} />
      </button>
      {showInput && (
        <div
          css={tw`absolute right-0 top-[calc(100%+6px)] z-[200] flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-shades-200 bg-shades-0 p-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)]`}
        >
          <input
            ref={inputRef}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleApply()
              }

              if (e.key === 'Escape') {
                handleCancel()
              }
            }}
            placeholder="https://"
            css={tw`box-border h-8 w-[220px] rounded-md border border-solid border-shades-200 bg-shades-0 px-2.5 font-[inherit] text-[13px] text-shades-900 outline-none focus:border-primary-400`}
          />
          <button
            type="button"
            onClick={handleApply}
            css={tw`h-8 cursor-pointer whitespace-nowrap rounded-md border-0 bg-primary-500 px-3 text-xs font-semibold text-shades-0`}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={handleCancel}
            css={tw`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-shades-400`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

export function RichTextEditor({
  value,
  onChange,
  readOnly = false,
}: {
  value?: string
  onChange?: (html: string) => void
  readOnly?: boolean
}) {
  return (
    <div
      className="rte-wrap"
      style={readOnly ? { pointerEvents: 'none', opacity: 0.72 } : undefined}
    >
      <style>{richEditorStyles}</style>
      <EditorProvider>
        <Editor value={value || ''} onChange={(e) => onChange?.(e.target.value)}>
          <Toolbar>
            <IcUndo />
            <IcRedo />
            <Separator />
            <BlockStyleSelect />
            <Separator />
            <IcBold />
            <IcItalic />
            <IcUnderline />
            <IcStrike />
            <Separator />
            <IcOL />
            <IcUL />
            <IcOutdent />
            <IcIndent />
            <Separator />
            <RteLinkButton />
            <IcClear />
          </Toolbar>
        </Editor>
      </EditorProvider>
    </div>
  )
}
